import React, { useState } from 'react';
import { Camera, IdCard, BookUser, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

const ImageSlot = ({ label, hint, img, onPick, capture, testid }) => (
  <div>
    <div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>
    {hint && <div className="text-[11px] text-slate-500 mb-1.5">{hint}</div>}
    <label className="block cursor-pointer">
      <input type="file" accept="image/*" capture={capture} className="hidden" onChange={onPick} data-testid={`identity-input-${testid}`} />
      {img ? (
        <img src={img.preview} alt={testid} className="rounded-xl border-2 border-emerald-300 max-h-44 w-full object-contain bg-slate-50" data-testid={`identity-preview-${testid}`} />
      ) : (
        <div className="rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0C3C2D]/50 p-6 text-center text-slate-400 transition-colors">
          <Camera className="w-7 h-7 mx-auto mb-1" />
          <span className="text-xs">{label}</span>
        </div>
      )}
    </label>
  </div>
);

const TypeButton = ({ active, icon: Icon, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 rounded-xl border-2 p-3 flex flex-col items-center gap-1.5 text-sm font-semibold transition-colors ${
      active ? 'border-[#0C3C2D] bg-[#0C3C2D] text-[#E8C874]' : 'border-slate-200 text-[#0C3C2D] hover:border-[#0C3C2D]/50'
    }`}
    data-testid={testid}
  >
    <Icon className="w-6 h-6" />{label}
  </button>
);

/* ★ Fase 73 — tipo documento: passaporto (1 foto) o carta d'identità (fronte + retro) + selfie col documento in mano */
export const IdentityStepV2 = ({ T, busy, pickImage, onSubmit, serverError }) => {
  const [docType, setDocType] = useState(null);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const isCard = docType === 'id_card';
  const ready = docType && front && selfie && (!isCard || back);

  const choose = (t) => { setDocType(t); setFront(null); setBack(null); };
  const submit = () => onSubmit({
    doc_type: docType, doc_image_b64: front.b64, doc_back_image_b64: isCard ? back.b64 : null, selfie_image_b64: selfie.b64,
  });

  return (
    <div className="bg-white rounded-2xl border border-[#e2ddcc] p-6 md:p-8 max-w-lg mx-auto space-y-5" data-testid="identity-step-v2">
      <div className="text-center">
        <IdCard className="w-10 h-10 mx-auto text-[#0C3C2D]" />
        <div className="font-serif text-xl text-[#0C3C2D] mt-1">{T.idTitle}</div>
        <p className="text-sm text-slate-500 mt-1">{T.idDescV2}</p>
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1.5">{T.idTypeTitle}</div>
        <div className="flex gap-3">
          <TypeButton active={docType === 'passport'} icon={BookUser} label={T.idPassport} onClick={() => choose('passport')} testid="identity-type-passport" />
          <TypeButton active={isCard} icon={IdCard} label={T.idIdCard} onClick={() => choose('id_card')} testid="identity-type-id_card" />
        </div>
      </div>
      {docType && (
        <>
          <ImageSlot label={isCard ? T.idCardFront : T.idPassportFront} img={front} onPick={(e) => pickImage(e, setFront)} capture="environment" testid="doc" />
          {isCard && <ImageSlot label={T.idCardBack} img={back} onPick={(e) => pickImage(e, setBack)} capture="environment" testid="doc_back" />}
          <ImageSlot label={`${isCard ? 3 : 2} · ${T.idSelfieDocLabel}`} hint={T.idSelfieDocHint} img={selfie} onPick={(e) => pickImage(e, setSelfie)} capture="user" testid="selfie" />
        </>
      )}
      {serverError && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2" data-testid="identity-server-error">
          <AlertTriangle className="w-4 h-4 shrink-0" />{serverError}
        </div>
      )}
      <Button className="w-full h-11 bg-[#0C3C2D] text-[#E8C874] hover:bg-[#0C3C2D]/90" onClick={submit} disabled={busy || !ready} data-testid="identity-submit-btn">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {T.idBusy}</> : T.idSubmit}
      </Button>
      <p className="text-[10px] text-slate-400 text-center">{T.idPrivacy}</p>
    </div>
  );
};
